import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Modal } from '@mui/material';
import { format } from 'date-fns';
import { isNil, omit, omitBy, pick } from 'lodash';
import { CreateScheduledETLJobBody, ETLExecutionModeEnum, ScheduleETLJob } from '~/api/types';
import { useCreateScheduledJobMutation, useEditScheduledJobMutation, useGetScheduledETLJobQuery } from '~/api/v2/hooks';
import { ButtonSpinner, ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle } from '~/components/styles';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { combineDateTime } from '~/utils';
import { scheduleETLJobSchema } from '~/validation';
import { ScheduleJobForm } from '.';
import { initialDefaults } from './types';

export interface ScheduleJobModalProps {
  payload: {
    id: string;
    jobId?: number;
  };
}

const ScheduleJobModal: FC<ScheduleJobModalProps> = ({ payload: { id, jobId } }) => {
  const projectId = +useParams().projectid!;
  const dispatch = useAppDispatch();
  const [createScheduledJob, { isLoading: isJobCreating }] = useCreateScheduledJobMutation({});
  const [editScheduledJob, { isLoading: isJobEditing }] = useEditScheduledJobMutation({});
  const { data: jobData } = useGetScheduledETLJobQuery({ projectId, jobId: jobId! }, { skip: !jobId || !projectId });

  const formMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: { ...initialDefaults, projectId },
    resolver: yupResolver(scheduleETLJobSchema),
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, dirtyFields },
  } = formMethods;

  useEffect(() => {
    if (jobId && jobData) {
      reset({
        ...pick(jobData, ['file_name', 'notebook_id', 'name', 'frequency']),
        execution_mode: jobData?.frequency ? ETLExecutionModeEnum.SCHEDULE : ETLExecutionModeEnum.ON_DEMAND,
        start_date: jobData?.start_date ? format(new Date(`${jobData.start_date}Z`), 'yyyy-MM-dd') : null,
        time: jobData?.start_date ? format(new Date(`${jobData.start_date}Z`), 'HH:mm') : null,
        end_date: jobData?.end_date ? format(new Date(`${jobData.end_date}Z`), 'yyyy-MM-dd') : null,
        projectId,
        jobId,
      });
    }
  }, [jobId, jobData, reset, projectId]);

  const handleClose = () => {
    dispatch(closeModalById(id));
    reset({ ...initialDefaults, projectId });
  };

  const onSubmit = async (data: ScheduleETLJob) => {
    const body = omitBy(
      {
        ...omit(data, ['execution_mode', 'time', 'start_date']),
        projectId,
        jobId,
        start_date: data.start_date && data.time ? combineDateTime(data.start_date, data.time).toISOString() : null,
      },
      isNil,
    ) as CreateScheduledETLJobBody & { jobId: number };

    if (jobId) {
      await editScheduledJob({ ...pick(body, Object.keys(dirtyFields)), projectId, jobId });
    } else {
      await createScheduledJob(omit(body, 'jobId'));
    }

    handleClose();
  };

  return (
    <Modal open onClose={handleClose}>
      <Box sx={modalContainerStyle}>
        <ModalHeader onClose={handleClose} title={[`${jobId ? 'Edit' : 'Schedule'} an ETL Job`]} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ScheduleJobForm />
            <Box sx={modalActionBlockStyles}>
              <CustomButton buttonType="outlined" label="Cancel" onClick={() => dispatch(closeModalById(id))} />
              <CustomButton
                type="submit"
                disabled={!isValid || (!!jobId && !Object.keys(dirtyFields).length) || isJobCreating || isJobEditing}
              >
                {jobId ? 'Save' : 'Schedule'}
                {(isJobCreating || isJobEditing) && <ButtonSpinner />}
              </CustomButton>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default ScheduleJobModal;
